import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import IntegrationActions from '../IntegrationActions';
import { asList, getJson, money, shortDate, statusClass } from '../helpers';
import { reportPath } from '../reports';
import CommitmentSyncModal from './CommitmentSyncModal';
import InvoiceSyncModal from './InvoiceSyncModal';

function scoped(data, projectVuid) {
  return asList(data).filter((row) => !row.project_vuid || row.project_vuid === projectVuid);
}

function commitmentAmount(row) {
  return Number(
    row.commitment_amount ?? (Number(row.original_amount || 0) + Number(row.change_orders_amount || 0))
  );
}

function billingAmount(row) {
  return Number(row.total_amount || 0) + Number(row.retention_held || 0);
}

function ProjectSettingsGear({ projectVuid }) {
  return (
    <Link
      className="pc-settings-icon"
      to={`/procore/projects/${projectVuid}/settings?project=${projectVuid}`}
      title="Project Settings"
      aria-label="Project Settings"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
      </svg>
    </Link>
  );
}

function AnalysisAlertIcon({ projectVuid, counts }) {
  const high = counts?.high || 0;
  const medium = counts?.medium || 0;
  const total = counts?.total || 0;
  if (total <= 0) return null;
  const severity = high > 0 ? 'high' : medium > 0 ? 'medium' : 'low';
  const label = `${total} project analysis alert${total === 1 ? '' : 's'}`;
  return (
    <Link
      className={`pc-alert-icon is-${severity}`}
      to={reportPath(projectVuid, 'project-analysis')}
      title={label}
      aria-label={label}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
      </svg>
    </Link>
  );
}

function ObjectSection({
  title,
  count,
  countReady,
  expanded,
  onToggle,
  addTo,
  onSync,
  ready,
  rows,
  empty,
  columns,
  onRow,
}) {
  return (
    <div className={`pc-card${expanded ? '' : ' is-collapsed'}`}>
      <div className="pc-panel-h">
        <button
          type="button"
          className="pc-section-toggle"
          onClick={onToggle}
          aria-expanded={expanded}
        >
          <span className={`pc-gl-chevron${expanded ? ' is-open' : ''}`} aria-hidden="true">
            ▸
          </span>
          <span className="pc-section-label">
            {title}{' '}
            <span className="pc-section-count">({countReady ? count : '…'})</span>
          </span>
        </button>
        <div className="pc-section-add">
          {addTo ? (
            <Link className="pc-btn pc-btn-sm pc-section-add-btn" to={addTo} aria-label={`Add ${title}`}>
              Add
            </Link>
          ) : onSync ? (
            <button
              type="button"
              className="pc-btn pc-btn-sm pc-section-add-btn"
              onClick={onSync}
              aria-label={`Sync ${title} from Procore`}
            >
              Sync
            </button>
          ) : null}
        </div>
      </div>
      {expanded ? (
        !ready ? (
          <div className="pc-empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="pc-empty">{empty}</div>
        ) : (
          <div className="pc-table-scroll">
            <table className="pc-table">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column.key} className={column.money ? 'pc-right' : undefined}>
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className={onRow ? 'pc-clickable' : undefined}
                    onClick={() => onRow && onRow(row)}
                  >
                    {columns.map((column) => (
                      <td key={column.key} className={column.money ? 'pc-right' : undefined}>
                        {column.status ? (
                          <span className={`pc-pill ${statusClass(row[column.key])}`}>{row[column.key] || '—'}</span>
                        ) : column.money ? (
                          money(row[column.key])
                        ) : (
                          row[column.key] || '—'
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </div>
  );
}

const SECTION_READY = {
  commitments: false,
  budgets: false,
  contracts: false,
  billings: false,
  invoices: false,
  labor: false,
  laborRates: false,
  expenses: false,
};

export default function ProjectHomePage({ projectVuid, initialProject }) {
  const navigate = useNavigate();
  const [project, setProject] = useState(initialProject || null);
  const [commitments, setCommitments] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [billings, setBillings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [laborCosts, setLaborCosts] = useState([]);
  const [laborRateLines, setLaborRateLines] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [alertCounts, setAlertCounts] = useState(null);
  const [counts, setCounts] = useState(null);
  const [totals, setTotals] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [syncModal, setSyncModal] = useState(null);
  const [projectSynced, setProjectSynced] = useState(null);
  const [summaryRefresh, setSummaryRefresh] = useState(0);
  const [listRefresh, setListRefresh] = useState(0);
  const requestedRef = useRef({});
  const projectVuidRef = useRef(projectVuid);
  const listRefreshRef = useRef(listRefresh);
  projectVuidRef.current = projectVuid;
  listRefreshRef.current = listRefresh;
  const [ready, setReady] = useState({
    project: Boolean(initialProject),
    ...SECTION_READY,
  });

  useEffect(() => {
    if (initialProject?.vuid === projectVuid) {
      setProject(initialProject);
      setReady((current) => ({ ...current, project: true }));
    }
  }, [initialProject, projectVuid]);

  useEffect(() => {
    const requestedFor = projectVuid;
    getJson(`/api/projects/${requestedFor}/home-summary`)
      .then((data) => {
        if (projectVuidRef.current !== requestedFor) return;
        setCounts(data?.counts || {});
        setTotals(data?.totals || {});
      })
      .catch(() => {
        if (projectVuidRef.current !== requestedFor) return;
        setCounts({});
        setTotals({});
      });
  }, [projectVuid, summaryRefresh]);

  useEffect(() => {
    let cancelled = false;
    setProjectSynced(null);
    getJson('/api/external-system-ids', { object_type: 'project', object_vuid: projectVuid })
      .then((data) => {
        if (cancelled) return;
        const mapped = asList(data).some((row) => {
          const type = String(row.integration?.integration_type || row.integration_type || '').toLowerCase();
          return type === 'procore' && Boolean(row.external_id);
        });
        setProjectSynced(mapped);
      })
      .catch(() => {
        if (!cancelled) setProjectSynced(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  useEffect(() => {
    let cancelled = false;
    setCommitments([]);
    setBudgets([]);
    setContracts([]);
    setBillings([]);
    setInvoices([]);
    setLaborCosts([]);
    setLaborRateLines([]);
    setExpenses([]);
    setExpanded({});
    setSyncModal(null);
    requestedRef.current = {};
    setReady({
      project: initialProject?.vuid === projectVuid,
      ...SECTION_READY,
    });

    if (initialProject?.vuid === projectVuid) {
      setProject(initialProject);
    } else {
      setProject(null);
      getJson(`/api/projects/${projectVuid}`)
        .then((data) => {
          if (!cancelled) setProject(data);
        })
        .catch(() => {
          if (!cancelled) setProject(null);
        })
        .finally(() => {
          if (!cancelled) setReady((current) => ({ ...current, project: true }));
        });
    }

    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  useEffect(() => {
    const loaders = {
      commitments: () => getJson('/api/project-commitments', { project_vuid: projectVuid }).then((data) => scoped(data, projectVuid)),
      budgets: () => getJson('/api/project-budgets', { project_vuid: projectVuid }).then((data) => scoped(data, projectVuid)),
      contracts: () => getJson('/api/project-contracts', { project_vuid: projectVuid }).then((data) => scoped(data, projectVuid)),
      billings: () => getJson('/api/project-billings', { project_vuid: projectVuid }).then((data) => scoped(data, projectVuid)),
      invoices: () => getJson('/api/ap-invoices', { project_vuid: projectVuid }).then((data) => scoped(data, projectVuid)),
      labor: () => getJson('/api/labor-costs', { project_vuid: projectVuid }).then((data) => scoped(data, projectVuid)),
      laborRates: () => getJson('/api/labor-rate-schedules', { project_vuid: projectVuid }).then((data) => {
        const schedules = asList(data).filter((row) => row.project_vuid === projectVuid);
        return schedules.flatMap((schedule) => asList(schedule.lines));
      }),
      expenses: () => getJson('/api/project-expenses', { project_vuid: projectVuid }).then((data) => scoped(data, projectVuid)),
    };
    const apply = {
      commitments: setCommitments,
      budgets: setBudgets,
      contracts: setContracts,
      billings: setBillings,
      invoices: setInvoices,
      labor: setLaborCosts,
      laborRates: setLaborRateLines,
      expenses: setExpenses,
    };

    const keys = Object.keys(loaders).filter((key) => expanded[key] && !requestedRef.current[key]);
    if (!keys.length) return undefined;

    const epoch = listRefresh;
    const requestedFor = projectVuid;
    const stillCurrent = () => (
      listRefreshRef.current === epoch && projectVuidRef.current === requestedFor
    );
    keys.forEach((key) => {
      requestedRef.current[key] = true;
      loaders[key]()
        .then((rows) => {
          if (!stillCurrent()) return;
          apply[key](rows);
        })
        .catch(() => {
          if (!stillCurrent()) return;
          apply[key]([]);
        })
        .finally(() => {
          if (stillCurrent()) setReady((current) => ({ ...current, [key]: true }));
        });
    });
  }, [expanded, projectVuid, listRefresh]);

  useEffect(() => {
    let cancelled = false;
    setAlertCounts(null);
    getJson('/api/project-analysis', { project_vuid: projectVuid })
      .then((data) => {
        if (cancelled) return;
        const row = Array.isArray(data?.projects) ? data.projects[0] : null;
        setAlertCounts(row?.finding_counts || { high: 0, medium: 0, low: 0, total: 0 });
      })
      .catch(() => {
        if (!cancelled) setAlertCounts(null);
      });
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  const toggleSection = (key) => {
    setExpanded((current) => ({ ...current, [key]: !current[key] }));
  };

  const reloadProjectMapping = () => {
    getJson('/api/external-system-ids', { object_type: 'project', object_vuid: projectVuid })
      .then((data) => {
        const mapped = asList(data).some((row) => {
          const type = String(row.integration?.integration_type || row.integration_type || '').toLowerCase();
          return type === 'procore' && Boolean(row.external_id);
        });
        setProjectSynced(mapped);
      })
      .catch(() => setProjectSynced(false));
  };

  const handleSyncComplete = () => {
    setSyncModal(null);
    requestedRef.current = {};
    setSummaryRefresh((value) => value + 1);
    setListRefresh((value) => value + 1);
  };

  const sectionCount = (key, rows) => (ready[key] ? rows.length : counts?.[key] ?? 0);
  const commitmentTotal = totals?.commitments ?? commitments.reduce((sum, row) => sum + commitmentAmount(row), 0);
  const invoiceTotal = totals?.invoices ?? invoices.reduce((sum, row) => sum + Number(row.total_amount || row.subtotal || 0), 0);
  const billingTotal = totals?.billings ?? billings.reduce((sum, row) => sum + billingAmount(row), 0);
  const budgetTotal = totals?.budgets ?? budgets.reduce(
    (sum, row) => sum + Number(row.budget_amount || row.current_amount || row.original_amount || 0),
    0
  );
  const currentBudget = Number(project?.current_budget || 0) || budgetTotal;
  const currentContract = Number(project?.total_contract_amount || project?.total_contract_value || 0);
  const countsReady = counts != null;
  const base = `/procore/projects/${projectVuid}`;
  const tileValue = (isReady, value) => (isReady ? money(value) : '…');
  const tileCount = (key, rows, singular, plural) => {
    const n = sectionCount(key, rows);
    if (!(countsReady || ready[key])) return 'Loading…';
    return `${n} ${n === 1 ? singular : plural}`;
  };

  return (
    <div className="pc-page">
      <div className="pc-crumb">
        <Link to="/procore">Portfolio</Link>
        {' / '}
        {project?.project_name || 'Project'}
      </div>
      <div className="pc-title-row">
        <div>
          <div className="pc-title-heading">
            <h1>{project?.project_name || 'Home'}</h1>
            <ProjectSettingsGear projectVuid={projectVuid} />
            <AnalysisAlertIcon projectVuid={projectVuid} counts={alertCounts} />
          </div>
          {project?.project_number ? (
            <p className="pc-muted" style={{ margin: '4px 0 0' }}>
              {project.project_number}
              {project.status ? ` · ${project.status}` : ''}
              {project.start_date ? ` · ${shortDate(project.start_date)}` : ''}
              {project.end_date ? ` – ${shortDate(project.end_date)}` : ''}
            </p>
          ) : null}
        </div>
        <IntegrationActions
          projectVuid={projectVuid}
          sendKind={projectSynced === false ? 'project' : undefined}
          sendPayload={{ project_vuid: projectVuid }}
          onComplete={reloadProjectMapping}
        />
      </div>
      <div className="pc-grid">
        <div className="pc-card pc-stat">
          <div className="pc-label">Current Contract</div>
          <div className="pc-value">{tileValue(ready.project, currentContract)}</div>
          <Link to={`${base}/contracts?project=${projectVuid}`}>
            {countsReady || ready.contracts
              ? tileCount('contracts', contracts, 'contract', 'contracts')
              : 'Contract + change orders'}
          </Link>
        </div>
        <div className="pc-card pc-stat">
          <div className="pc-label">Current Budget</div>
          <div className="pc-value">{tileValue(ready.project || countsReady || ready.budgets, currentBudget)}</div>
          <Link to={`${base}/budget?project=${projectVuid}`}>
            {countsReady || ready.budgets
              ? tileCount('budgets', budgets, 'budget', 'budgets')
              : 'Open Budget'}
          </Link>
        </div>
        <div className="pc-card pc-stat">
          <div className="pc-label">Commitments</div>
          <div className="pc-value">{tileValue(countsReady || ready.commitments, commitmentTotal)}</div>
          <Link to={`${base}/commitments`}>
            {tileCount('commitments', commitments, 'contract', 'contracts')}
          </Link>
        </div>
        <div className="pc-card pc-stat">
          <div className="pc-label">Subcontractor Invoices</div>
          <div className="pc-value">{tileValue(countsReady || ready.invoices, invoiceTotal)}</div>
          <Link to={`${base}/invoicing/subcontractor?project=${projectVuid}`}>
            {tileCount('invoices', invoices, 'invoice', 'invoices')}
          </Link>
        </div>
        <div className="pc-card pc-stat">
          <div className="pc-label">Owner Billings</div>
          <div className="pc-value">{tileValue(countsReady || ready.billings, billingTotal)}</div>
          <Link to={`${base}/invoicing?project=${projectVuid}`}>
            {tileCount('billings', billings, 'application', 'applications')}
          </Link>
        </div>
      </div>
      <div className="pc-sections">
        <ObjectSection
          title="Commitments"
          count={sectionCount('commitments', commitments)}
          countReady={countsReady || ready.commitments}
          expanded={Boolean(expanded.commitments)}
          onToggle={() => toggleSection('commitments')}
          onSync={() => setSyncModal('commitment')}
          ready={ready.commitments}
          empty="No commitments on this project yet."
          onRow={(row) => navigate(`${base}/commitments/${row.id}`)}
          columns={[
            { key: 'number', label: '#' },
            { key: 'title', label: 'Title' },
            { key: 'vendor', label: 'Vendor' },
            { key: 'amount', label: 'Amount', money: true },
            { key: 'status', label: 'Status', status: true },
          ]}
          rows={commitments.map((row) => ({
            id: row.vuid,
            number: row.commitment_number,
            title: row.commitment_name,
            vendor: row.vendor?.vendor_name || row.vendor_name,
            amount: commitmentAmount(row),
            status: row.status,
          }))}
        />
        <ObjectSection
          title="Project Budgets"
          count={sectionCount('budgets', budgets)}
          countReady={countsReady || ready.budgets}
          expanded={Boolean(expanded.budgets)}
          onToggle={() => toggleSection('budgets')}
          addTo={`${base}/budget?project=${projectVuid}&create=true`}
          ready={ready.budgets}
          empty="No budgets on this project yet."
          onRow={(row) => navigate(`${base}/budget/${row.id}?project=${projectVuid}&budget=${row.id}`)}
          columns={[
            { key: 'description', label: 'Description' },
            { key: 'type', label: 'Type' },
            { key: 'date', label: 'Date' },
            { key: 'amount', label: 'Amount', money: true },
            { key: 'status', label: 'Status', status: true },
          ]}
          rows={budgets.map((row) => ({
            id: row.vuid,
            description: row.description || row.budget_type,
            type: row.budget_type,
            date: shortDate(row.budget_date),
            amount: Number(row.budget_amount || 0),
            status: row.status || (row.finalized ? 'finalized' : 'active'),
          }))}
        />
        <ObjectSection
          title="Labor Rate Schedule"
          count={sectionCount('laborRates', laborRateLines)}
          countReady={countsReady || ready.laborRates}
          expanded={Boolean(expanded.laborRates)}
          onToggle={() => toggleSection('laborRates')}
          addTo={`${base}/labor-rate-schedule?project=${projectVuid}`}
          ready={ready.laborRates}
          empty="No labor rates on this project yet."
          onRow={() => navigate(`${base}/labor-rate-schedule?project=${projectVuid}`)}
          columns={[
            { key: 'costCode', label: 'Cost Code' },
            { key: 'laborClass', label: 'Labor Class' },
            { key: 'costRate', label: 'Cost Rate' },
            { key: 'billingRate', label: 'Billing Rate' },
            { key: 'effectiveDate', label: 'Effective Date' },
            { key: 'status', label: 'Status', status: true },
          ]}
          rows={laborRateLines.map((row) => ({
            id: row.vuid,
            costCode: row.cost_code
              ? [row.cost_code.code, row.cost_code.description].filter(Boolean).join(' - ')
              : row.cost_code_vuid,
            laborClass: row.labor_class,
            costRate: row.status === 'incomplete' ? '—' : money(row.cost_rate),
            billingRate: row.status === 'incomplete' ? '—' : money(row.billing_rate),
            effectiveDate: row.status === 'incomplete' ? 'Required' : (row.effective_date || ''),
            status: row.status === 'incomplete' ? 'Needs rates' : 'Ready',
          }))}
        />
        <ObjectSection
          title="Project Contracts"
          count={sectionCount('contracts', contracts)}
          countReady={countsReady || ready.contracts}
          expanded={Boolean(expanded.contracts)}
          onToggle={() => toggleSection('contracts')}
          addTo={`${base}/contracts?project=${projectVuid}&create=true`}
          ready={ready.contracts}
          empty="No contracts on this project yet."
          onRow={(row) => navigate(`${base}/contracts/${row.id}?project=${projectVuid}&contract=${row.id}`)}
          columns={[
            { key: 'number', label: '#' },
            { key: 'title', label: 'Title' },
            { key: 'customer', label: 'Customer' },
            { key: 'amount', label: 'Amount', money: true },
            { key: 'status', label: 'Status', status: true },
          ]}
          rows={contracts.map((row) => ({
            id: row.vuid,
            number: row.contract_number,
            title: row.contract_name || row.description,
            customer: row.customer?.customer_name || row.customer_name,
            amount: Number(row.total_amount || row.contract_amount || row.amount || 0),
            status: row.status,
          }))}
        />
        <ObjectSection
          title="Project Billings"
          count={sectionCount('billings', billings)}
          countReady={countsReady || ready.billings}
          expanded={Boolean(expanded.billings)}
          onToggle={() => toggleSection('billings')}
          addTo={`${base}/invoicing?project=${projectVuid}&create=true`}
          ready={ready.billings}
          empty="No owner billings on this project yet."
          onRow={() => navigate(`${base}/invoicing?project=${projectVuid}`)}
          columns={[
            { key: 'number', label: '#' },
            { key: 'contract', label: 'Contract' },
            { key: 'customer', label: 'Customer' },
            { key: 'amount', label: 'Amount', money: true },
            { key: 'date', label: 'Date' },
            { key: 'status', label: 'Status', status: true },
          ]}
          rows={billings.map((row) => ({
            id: row.vuid,
            number: row.billing_number,
            contract: row.contract?.contract_number || row.contract_number,
            customer: row.customer?.customer_name || row.customer_name,
            amount: billingAmount(row),
            date: shortDate(row.billing_date),
            status: row.status,
          }))}
        />
        <ObjectSection
          title="AP Invoices"
          count={sectionCount('invoices', invoices)}
          countReady={countsReady || ready.invoices}
          expanded={Boolean(expanded.invoices)}
          onToggle={() => toggleSection('invoices')}
          onSync={() => setSyncModal('invoice')}
          ready={ready.invoices}
          empty="No AP invoices on this project yet."
          onRow={(row) => navigate(`${base}/invoicing/ap/${row.id}`)}
          columns={[
            { key: 'number', label: '#' },
            { key: 'vendor', label: 'Vendor' },
            { key: 'amount', label: 'Amount', money: true },
            { key: 'date', label: 'Date' },
            { key: 'status', label: 'Status', status: true },
          ]}
          rows={invoices.map((row) => ({
            id: row.vuid,
            number: row.invoice_number,
            vendor: row.vendor_name || row.vendor?.vendor_name,
            amount: Number(row.total_amount || row.subtotal || 0),
            date: shortDate(row.invoice_date || row.date),
            status: row.status,
          }))}
        />
        <ObjectSection
          title="Labor Costs"
          count={sectionCount('labor', laborCosts)}
          countReady={countsReady || ready.labor}
          expanded={Boolean(expanded.labor)}
          onToggle={() => toggleSection('labor')}
          addTo={`${base}/labor-costs?project=${projectVuid}`}
          ready={ready.labor}
          empty="No labor costs on this project yet."
          onRow={() => navigate(`${base}/labor-costs?project=${projectVuid}`)}
          columns={[
            { key: 'employee', label: 'Employee' },
            { key: 'costCode', label: 'Cost Code' },
            { key: 'costType', label: 'Cost Type' },
            { key: 'amount', label: 'Amount', money: true },
            { key: 'date', label: 'Payroll Date' },
            { key: 'status', label: 'Status', status: true },
          ]}
          rows={laborCosts.map((row) => ({
            id: row.vuid,
            employee: row.employee_id || row.employee?.employee_name || row.employee_name,
            costCode: row.cost_code?.code || row.cost_code,
            costType: row.cost_type?.cost_type || row.cost_type?.abbreviation,
            amount: Number(row.amount || row.total_amount || 0),
            date: shortDate(row.payroll_date || row.date),
            status: row.status,
          }))}
        />
        <ObjectSection
          title="Project Charges"
          count={sectionCount('expenses', expenses)}
          countReady={countsReady || ready.expenses}
          expanded={Boolean(expanded.expenses)}
          onToggle={() => toggleSection('expenses')}
          addTo={`${base}/project-expenses?project=${projectVuid}`}
          ready={ready.expenses}
          empty="No project expenses on this project yet."
          onRow={() => navigate(`${base}/project-expenses?project=${projectVuid}`)}
          columns={[
            { key: 'number', label: '#' },
            { key: 'party', label: 'Vendor / Employee' },
            { key: 'description', label: 'Description' },
            { key: 'amount', label: 'Amount', money: true },
            { key: 'date', label: 'Date' },
            { key: 'status', label: 'Status', status: true },
          ]}
          rows={expenses.map((row) => ({
            id: row.vuid,
            number: row.expense_number,
            party: row.vendor?.vendor_name || row.vendor_name || row.employee?.employee_name,
            description: row.description,
            amount: Number(row.amount || row.total_amount || 0),
            date: shortDate(row.expense_date || row.date),
            status: row.status,
          }))}
        />
      </div>
      {syncModal === 'commitment' ? (
        <CommitmentSyncModal
          projectVuid={projectVuid}
          onClose={() => setSyncModal(null)}
          onComplete={handleSyncComplete}
        />
      ) : null}
      {syncModal === 'invoice' ? (
        <InvoiceSyncModal
          projectVuid={projectVuid}
          onClose={() => setSyncModal(null)}
          onComplete={handleSyncComplete}
        />
      ) : null}
    </div>
  );
}
